import React, { useState } from 'react';
import { X, Palette, Save, Eye } from 'lucide-react';

const CustomThemeCreator = ({ 
  isOpen, 
  onClose, 
  onSave,
  editingTheme = null 
}) => {
  const [themeName, setThemeName] = useState(editingTheme?.name || '');
  const [colors, setColors] = useState(editingTheme?.colors || {
    primary: '#3B82F6',
    secondary: '#1E40AF', 
    accent: '#93C5FD',
    background: '#FFFFFF',
    text: '#1F2937'
  });
  const [showPreview, setShowPreview] = useState(true);

  if (!isOpen) return null;

  const handleColorChange = (colorKey, value) => {
    setColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const handleSave = () => {
    if (!themeName.trim()) {
      alert('Please enter a theme name');
      return;
    }

    const newTheme = {
      id: editingTheme?.id || `custom-${Date.now()}`,
      name: themeName.trim(),
      colors,
      createdAt: editingTheme?.createdAt || new Date(),
      updatedAt: new Date()
    };

    onSave(newTheme);
    onClose();
    
    // Reset form
    setThemeName('');
    setColors({
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#93C5FD', 
      background: '#FFFFFF',
      text: '#1F2937'
    });
  };

  const colorInputs = [
    { key: 'primary', label: 'Primary', description: 'Main brand color' },
    { key: 'secondary', label: 'Secondary', description: 'Supporting brand color' },
    { key: 'accent', label: 'Accent', description: 'Highlights and borders' },
    { key: 'background', label: 'Background', description: 'Page background' },
    { key: 'text', label: 'Text', description: 'Main text color' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Palette className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {editingTheme ? 'Edit Custom Theme' : 'Create Custom Theme'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex overflow-hidden" style={{ height: '70vh' }}>
          {/* Left Panel - Controls */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Theme Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme Name
              </label>
              <input
                type="text"
                value={themeName}
                onChange={(e) => setThemeName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., My Brand Colors, Summer Vibes"
                maxLength={30}
              />
            </div>

            {/* Color Inputs */}
            <div className="space-y-6">
              <h3 className="text-sm font-medium text-gray-900">Colors</h3>
              {colorInputs.map(({ key, label, description }) => (
                <div key={key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer relative overflow-hidden"
                      style={{ backgroundColor: colors[key] }}
                      onClick={() => document.getElementById(`color-${key}`).click()}
                    >
                      <input
                        id={`color-${key}`}
                        type="color"
                        value={colors[key]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={colors[key]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                        placeholder="#FFFFFF"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                      <p className="text-xs text-gray-500 mt-1">{description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Preview */}
          {showPreview && (
            <div className="w-96 border-l bg-gray-50 p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900">Live Preview</h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
              
              {/* Theme Preview */}
              <div className="space-y-4">
                {/* Color Palette */}
                <div>
                  <h4 className="text-xs font-medium text-gray-600 mb-2">Color Palette</h4>
                  <div className="flex space-x-2">
                    {colorInputs.map(({ key, label }) => (
                      <div key={key} className="flex-1">
                        <div
                          className="w-full h-12 rounded-lg border border-gray-200"
                          style={{ backgroundColor: colors[key] }}
                          title={`${label}: ${colors[key]}`}
                        />
                        <p className="text-xs text-gray-500 mt-1 text-center">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sample Website Section */}
                <div>
                  <h4 className="text-xs font-medium text-gray-600 mb-2">Sample Section</h4>
                  <div
                    className="rounded-lg border p-4 space-y-3"
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.accent,
                      color: colors.text
                    }}
                  >
                    <h5 className="text-lg font-semibold" style={{ color: colors.primary }}>
                      Sample Heading
                    </h5>
                    <p className="text-sm">
                      This is how your content will look with the selected colors.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        className="px-3 py-2 text-xs rounded font-medium text-white"
                        style={{ backgroundColor: colors.primary }}
                      >
                        Primary Button
                      </button>
                      <button
                        className="px-3 py-2 text-xs rounded font-medium border"
                        style={{
                          color: colors.secondary,
                          borderColor: colors.secondary
                        }}
                      >
                        Secondary Button
                      </button>
                    </div>
                    <div
                      className="p-2 rounded text-xs"
                      style={{
                        backgroundColor: colors.accent + '20',
                        color: colors.text
                      }}
                    >
                      This is an accent-colored highlight box.
                    </div>
                  </div>
                </div>

                {/* Accessibility Check */}
                <div>
                  <h4 className="text-xs font-medium text-gray-600 mb-2">Quick Accessibility Check</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Text on Background:</span>
                      <span className={getContrastRating(colors.text, colors.background)}>
                        {getContrastText(colors.text, colors.background)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Primary on Background:</span>
                      <span className={getContrastRating(colors.primary, colors.background)}>
                        {getContrastText(colors.primary, colors.background)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-800"
          >
            <Eye className="w-4 h-4 mr-1" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!themeName.trim()}
              className="px-4 py-2 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              <Save className="w-4 h-4 mr-1" />
              {editingTheme ? 'Update Theme' : 'Create Theme'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions for accessibility check
function getContrastRating(color1, color2) {
  const ratio = getContrastRatio(color1, color2);
  if (ratio >= 7) return 'text-green-600';
  if (ratio >= 4.5) return 'text-yellow-600';
  return 'text-red-600';
}

function getContrastText(color1, color2) {
  const ratio = getContrastRatio(color1, color2);
  if (ratio >= 7) return 'AAA ✓';
  if (ratio >= 4.5) return 'AA ✓';
  return 'Poor ✗';
}

function getContrastRatio(color1, color2) {
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export default CustomThemeCreator;